import React, { useState, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import LoadingSpinner from '../components/LoadingSpinner';

const { FiMail, FiLock, FiEye, FiEyeOff, FiAlertCircle, FiUserPlus } = FiIcons;

const Login = () => {
  const { login, isAuthenticated, loading, error } = useAuth();
  const location = useLocation();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSignUp, setIsSignUp] = useState(false);
  const [signUpData, setSignUpData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [signUpError, setSignUpError] = useState(null);
  const [demoSetupComplete, setDemoSetupComplete] = useState(false);

  // Get custom settings from localStorage
  const [customSettings, setCustomSettings] = useState({
    loginLogo: localStorage.getItem('login_logo') || '',
    showLoginLogo: localStorage.getItem('login_logo_enabled') !== 'false',
    portalTitle: localStorage.getItem('portal_title') || 'Information Portal'
  });

  const from = location.state?.from?.pathname || '/';

  const demoAccounts = [
    { email: 'admin@company.com', role: 'Admin (Full Access)', password: 'password123' },
    { email: 'exec@company.com', role: 'Executive (All Companies)', password: 'password123' },
    { email: 'manager@brioche.com', role: 'Manager (Brioche Only)', password: 'password123' },
    { email: 'viewer@enjoy.com', role: 'Viewer (Enjoy Shops Only)', password: 'password123' }
  ];

  const createDemoUser = async (account) => {
    try {
      const { supabaseService } = await import('../services/supabaseService');
      await supabaseService.signUp(account.email, account.password, {
        name: account.role.split('(')[0].trim()
      });
      console.log(`Demo user ${account.email} created`);
    } catch (error) {
      console.log(`Demo user ${account.email} might already exist:`, error.message);
    }
  };

  const setupDemoUsers = async () => {
    if (demoSetupComplete) return;

    console.log('Setting up demo users...');
    try {
      for (const account of demoAccounts) {
        await createDemoUser(account);
      }
      setDemoSetupComplete(true);
    } catch (error) {
      console.error('Error setting up demo users:', error);
    }
  };

  // Auto-setup demo users on component mount
  useEffect(() => {
    setupDemoUsers();
  }, []);

  // Update settings when localStorage changes
  useEffect(() => {
    const handleStorageChange = () => {
      setCustomSettings({
        loginLogo: localStorage.getItem('login_logo') || '',
        showLoginLogo: localStorage.getItem('login_logo_enabled') !== 'false',
        portalTitle: localStorage.getItem('portal_title') || 'Information Portal'
      });
    };

    // Listen for storage changes
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  if (isAuthenticated) {
    return <Navigate to={from} replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await login(formData);
    } catch (err) {
      console.error('Login error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    if (signUpData.password !== signUpData.confirmPassword) {
      setSignUpError('Passwords do not match');
      return;
    }

    if (signUpData.password.length < 6) {
      setSignUpError('Password must be at least 6 characters');
      return;
    }

    setIsSubmitting(true);
    setSignUpError(null);

    try {
      // Import supabaseService for sign up
      const { supabaseService } = await import('../services/supabaseService');
      const result = await supabaseService.signUp(signUpData.email, signUpData.password, {
        name: signUpData.name
      });

      if (result.user) {
        alert('Account created successfully! Please check your email to verify your account, then sign in.');
      } else {
        alert('Account created successfully! You can now sign in.');
      }

      setIsSignUp(false);
      setSignUpData({ name: '', email: '', password: '', confirmPassword: '' });
      setFormData({ email: signUpData.email, password: '' });
    } catch (err) {
      console.error('Sign up error:', err);
      setSignUpError(err.message || 'Error creating account. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSignUpChange = (e) => {
    setSignUpData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-secondary-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-md w-full space-y-8"
      >
        <div>
          {/* Custom Logo or Default Icon */}
          <div className="mx-auto h-24 w-24 flex items-center justify-center">
            {customSettings.showLoginLogo && customSettings.loginLogo ? (
              <div className="w-24 h-24 rounded-lg bg-white shadow-sm border border-gray-200 flex items-center justify-center p-2">
                <img
                  src={customSettings.loginLogo}
                  alt="Portal Logo"
                  className="max-w-full max-h-full object-contain"
                  onError={(e) => {
                    // Fallback to default icon if logo fails to load
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
                <div 
                  className="h-12 w-12 bg-primary-600 rounded-lg flex items-center justify-center" 
                  style={{ display: 'none' }}
                >
                  <SafeIcon icon={FiLock} className="h-6 w-6 text-white" />
                </div>
              </div>
            ) : (
              <div className="h-12 w-12 bg-primary-600 rounded-lg flex items-center justify-center">
                <SafeIcon icon={FiLock} className="h-6 w-6 text-white" />
              </div>
            )}
          </div>
          
          <h2 className="mt-6 text-center text-3xl font-bold text-gray-900">
            {customSettings.portalTitle}
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            {isSignUp ? 'Create your account' : 'Sign in to access your dashboards'}
          </p>
        </div>

        {!isSignUp ? (
          <motion.form
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1 }}
            className="mt-8 space-y-6"
            onSubmit={handleSubmit}
          >
            <div className="space-y-4">
              <div>
                <label htmlFor="email" className="sr-only">
                  Email address
                </label>
                <div className="relative">
                  <SafeIcon icon={FiMail} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className="relative block w-full pl-10 pr-3 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-lg focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm"
                    placeholder="Email address"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="password" className="sr-only">
                  Password
                </label>
                <div className="relative">
                  <SafeIcon icon={FiLock} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    required
                    value={formData.password}
                    onChange={handleChange}
                    className="relative block w-full pl-10 pr-10 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-lg focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm"
                    placeholder="Password"
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    <SafeIcon icon={showPassword ? FiEyeOff : FiEye} className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </div>

            {error && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center space-x-2"
              >
                <SafeIcon icon={FiAlertCircle} className="h-5 w-5 text-red-400" />
                <span className="text-sm text-red-700">{error}</span>
              </motion.div>
            )}

            <div>
              <button
                type="submit"
                disabled={isSubmitting || loading}
                className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isSubmitting || loading ? (
                  <LoadingSpinner size="sm" className="text-white" />
                ) : (
                  'Sign in'
                )}
              </button>
            </div>

            <div className="text-center">
              <button
                type="button"
                onClick={() => setIsSignUp(true)}
                className="text-sm text-primary-600 hover:text-primary-500"
              >
                Don't have an account? Sign up
              </button>
            </div>
          </motion.form>
        ) : (
          <motion.form
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="mt-8 space-y-6"
            onSubmit={handleSignUp}
          >
            <div className="space-y-4">
              <div>
                <div className="relative">
                  <SafeIcon icon={FiUserPlus} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                  <input
                    name="name"
                    type="text"
                    required
                    value={signUpData.name}
                    onChange={handleSignUpChange}
                    className="relative block w-full pl-10 pr-3 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-lg focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm"
                    placeholder="Full name"
                  />
                </div>
              </div>
              <div>
                <div className="relative">
                  <SafeIcon icon={FiMail} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                  <input
                    name="email"
                    type="email"
                    required
                    value={signUpData.email}
                    onChange={handleSignUpChange}
                    className="relative block w-full pl-10 pr-3 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-lg focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm"
                    placeholder="Email address"
                  />
                </div>
              </div>
              <div>
                <div className="relative">
                  <SafeIcon icon={FiLock} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                  <input
                    name="password"
                    type="password"
                    required
                    value={signUpData.password}
                    onChange={handleSignUpChange}
                    className="relative block w-full pl-10 pr-3 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-lg focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm"
                    placeholder="Password (min. 6 characters)"
                  />
                </div>
              </div>
              <div>
                <div className="relative">
                  <SafeIcon icon={FiLock} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                  <input
                    name="confirmPassword"
                    type="password"
                    required
                    value={signUpData.confirmPassword}
                    onChange={handleSignUpChange}
                    className="relative block w-full pl-10 pr-3 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-lg focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm"
                    placeholder="Confirm password"
                  />
                </div>
              </div>
            </div>

            {signUpError && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center space-x-2"
              >
                <SafeIcon icon={FiAlertCircle} className="h-5 w-5 text-red-400" />
                <span className="text-sm text-red-700">{signUpError}</span>
              </motion.div>
            )}

            <div>
              <button
                type="submit"
                disabled={isSubmitting}
                className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isSubmitting ? (
                  <LoadingSpinner size="sm" className="text-white" />
                ) : (
                  'Create Account'
                )}
              </button>
            </div>

            <div className="text-center">
              <button
                type="button"
                onClick={() => setIsSignUp(false)}
                className="text-sm text-primary-600 hover:text-primary-500"
              >
                Already have an account? Sign in
              </button>
            </div>
          </motion.form>
        )}

        {!isSignUp && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="mt-8 bg-white rounded-lg shadow-sm border border-gray-200 p-4"
          >
            <h3 className="text-sm font-medium text-gray-900 mb-3">Demo Accounts</h3>
            <div className="space-y-2">
              {demoAccounts.map((account) => (
                <button
                  key={account.email}
                  onClick={() => setFormData({ email: account.email, password: account.password })}
                  className="w-full text-left p-2 text-xs bg-gray-50 hover:bg-gray-100 rounded border border-gray-200 transition-colors"
                >
                  <div className="font-medium text-gray-900">{account.email}</div>
                  <div className="text-gray-500">{account.role}</div>
                </button>
              ))}
            </div>
            <p className="text-xs text-gray-500 mt-3">
              Click any demo account to auto-fill login credentials
            </p>
          </motion.div>
        )}
      </motion.div>
    </div>
  );
};

export default Login;